// faqNew.tsx
import { Component } from 'react';
import Head from 'next/head';
import amcStyles from '../../styles/amcs.scss';

interface FaqData {
  question: string;
  answer: string;
}

interface GroupProps {
  data: FaqData;
  isExpanded: boolean;
  toggleFAQ: () => void;
  isLast: boolean;
}

function Group({ data, isExpanded, toggleFAQ, isLast }: GroupProps) {
  const { question, answer } = data;
  return (
    <div className="lil-relative">
      <div
        className="lil-flex lil-justify-between lil-items-center lil-py-5 lil-relative lil-cursor-pointer sm:lil-py-3"
        onClick={toggleFAQ}
        aria-expanded={isExpanded}
        role="button"
        tabIndex={0}
        aria-label={`${question} - Click to ${
          isExpanded ? 'collapse' : 'expand'
        }`}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleFAQ();
          }
        }}
      >
        <span className="lil-font-semibold lil-text-s-24 sm:lil-text-s-18">
          {question}
        </span>
        <span className="lil-flex-shrink-0 lil-ml-4">
          {isExpanded ? (
            <img
              src="http://cleartax-media.s3.amazonaws.com/cleartax/images/1742892758_upchevron.svg"
              alt="Collapse"
              width="24"
              height="24"
            />
          ) : (
            <img
              src="http://cleartax-media.s3.amazonaws.com/cleartax/images/1742892858_downchevron.svg"
              alt="Expand"
              width="24"
              height="24"
            />
          )}
        </span>
      </div>
      <div
        className={`lil-pb-6 ${isExpanded ? '' : 'lil-hidden'}`}
        aria-hidden={!isExpanded}
        id={`faq-answer-${question.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <p className="lil-font-normal lil-text-s-18 lil-text-grey-600 sm:lil-text-base">
          {answer}
        </p>
      </div>
      {!isLast && (
        <div className="lil-bottom-0 lil-left-0 lil-right-0 lil-absolute lil-h-px lil-bg-grey-300"></div>
      )}
    </div>
  );
}

interface FAQNewState {
  expandedFaqIndex: number | null;
}

interface FAQNewProps {
  faqs: FaqData[];
  mandate?: boolean;
  pageTitle?: string;
  pageDescription?: string;
}

class FAQNew extends Component<FAQNewProps, FAQNewState> {
  constructor(props: FAQNewProps) {
    super(props);
    this.state = {
      expandedFaqIndex: props.mandate ? 0 : null,
    };
  }

  toggleFAQ = (index: number) => {
    this.setState((prevState) => ({
      expandedFaqIndex: prevState.expandedFaqIndex === index ? null : index,
    }));
  };

  generateFAQSchema = () => {
    const { faqs } = this.props;

    const sanitizedFaqs = faqs.map((faq) => ({
      question: faq.question.replace(/"/g, '\\"').trim(),
      answer: faq.answer
        .replace(/"/g, '\\"')
        .replace(/<[^>]*>/g, '')
        .trim(),
    }));

    const faqSchemaString = `{
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        ${sanitizedFaqs
          .map(
            (faq) => `{
                "@type": "Question",
                "name": "${faq.question}",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${faq.answer}"
                }
              }`
          )
          .join(',')}
      ]
    }`;
    return faqSchemaString;
  };

  public render() {
    const { faqs, pageTitle, pageDescription } = this.props;
    const { expandedFaqIndex } = this.state;
    const faqSchemaString = this.generateFAQSchema();

    const metaDescription =
      pageDescription ||
      `Frequently asked questions about ${
        faqs && faqs.length > 0 ? faqs[0].question : 'our services'
      }. Find answers to common questions including ${
        faqs && faqs.length > 0
          ? faqs
              .slice(0, Math.min(3, faqs.length))
              .map((faq) => faq.question)
              .join(', ')
          : 'various topics'
      } and more.`;

    return (
      <div className="lil-w-full">
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: faqSchemaString }}
            key="faq-jsonld"
          />

          {pageTitle && <title>{pageTitle}</title>}
          <meta
            name="description"
            content={metaDescription}
            key="description"
          />

          <meta property="og:type" content="website" key="og:type" />
          {pageTitle && (
            <meta property="og:title" content={pageTitle} key="og:title" />
          )}
          <meta
            property="og:description"
            content={metaDescription}
            key="og:description"
          />

          <meta
            name="twitter:card"
            content="summary_large_image"
            key="twitter:card"
          />
          {pageTitle && (
            <meta
              name="twitter:title"
              content={pageTitle}
              key="twitter:title"
            />
          )}
          <meta
            name="twitter:description"
            content={metaDescription}
            key="twitter:description"
          />
        </Head>

        <section
          aria-labelledby="faq-heading"
          className="lil-mt-10 sm:lil-mb-6 sm:lil-mt-6"
        >
          <h2
            id="faq-heading"
            className={`${amcStyles['plan-title']} lil-text-s-28 sm:lil-text-s-20 lil-pb-2`}
          >
            Frequently Asked Questions
          </h2>
          <div
            className="lil-rounded-xl lil-bg-white"
            role="region"
            aria-label="FAQ Accordion"
          >
            {faqs.map((faq, index) => (
              <Group
                key={index}
                data={faq}
                isExpanded={expandedFaqIndex === index}
                toggleFAQ={() => this.toggleFAQ(index)}
                isLast={index === faqs.length - 1}
              />
            ))}
          </div>
        </section>
      </div>
    );
  }
}

export default FAQNew;



// WEBPACK FOOTER //
// ./components/plan/faqNew.tsx